/**
 * React Query Hook: Format Code
 *
 * Formats code (Apex, HTML, JS, CSS, JSON, XML) via the IDE service.
 * Uses prettier + prettier-plugin-apex on the backend.
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { ideServiceApi, ideAuthHeaders } from '@/services/ide';

// ---------- Types ----------

interface FormatCodeInput {
  code: string;
  language: string;
}

interface FormatCodeResponse {
  formatted: string;
  error?: string;
}

// ---------- API ----------

export async function formatCode(
  input: FormatCodeInput,
  orgId?: string | null,
): Promise<FormatCodeResponse> {
  const { data } = await ideServiceApi.post<FormatCodeResponse>('/format', input, {
    headers: ideAuthHeaders(orgId),
  });
  return data;
}

// ---------- Hook ----------

export interface UseFormatCodeOptions {
  onSuccess?: (data: FormatCodeResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to format code via the IDE service
 *
 * @example
 * const { mutateAsync: format, isPending } = useFormatCode({
 *   onSuccess: (data) => updateContent(tabId, data.formatted),
 * });
 * await format({ code: 'public class Foo{}', language: 'apex' });
 */
export function useFormatCode(
  options?: UseFormatCodeOptions,
): UseMutationResult<FormatCodeResponse, Error, FormatCodeInput> {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useMutation({
    mutationFn: (input: FormatCodeInput) => formatCode(input, orgId),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
